// Alert-Snapshot-Schreiber (Client).
//
// Premium-Alerts (Haltefrist, Portfolio-Wert) brauchen die ECHTEN Bestände und
// Lot-Daten des Users. Der Vercel-Cron hat keinen User-Kontext und darf die
// FIFO-Engine nicht duplizieren (Invariant 7: es gibt genau EINE FIFO-Engine,
// deterministisch). Deshalb berechnet der Client den Bestand aus der einen
// echten Engine (buildFifoPool → calcPortfolio) und legt einen kompakten
// Snapshot in Firestore ab, den der Cron nur noch liest.
//
// Firestore-Schema (Owner-only via bestehender users/{uid}/** Rule):
//   users/{uid}/alertState/snapshot = {
//     updatedAt: ISO-String,
//     currency:  'EUR',
//     holdings:  { [coingeckoId]: { sym, menge } },       // für portfolio_value
//     lots:      { [coingeckoId]: {                        // für holding_period
//        sym, tranches: [ { freeDate: 'YYYY-MM-DD', menge } ]
//     } },
//   }
//
// Privacy: enthält nur Symbole + Mengen + Daten des Users selbst, keine Preise,
// keine Klartext-Notizen. Liegt unter dem owner-only-geschützten users/{uid}.

import { doc, setDoc } from '../lib/fsCompat';
import { db } from '../firebase';
import { buildFifoPool } from '../utils/fifo';
import { calcPortfolio } from '../utils/portfolioCalc';
import { lotFreeDate } from '../utils/holdingPeriod';
import { COIN_MAP } from '../utils/coingecko';

// Fiat + NFT zählen nicht als bepreisbares Krypto-Asset (vgl. portfolioCalc).
const SNAPSHOT_EXCLUDE = new Set([
  'EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL', 'NFT',
]);

// lotFreeDate (Frei-Datum eines Lots) lebt jetzt zentral in holdingPeriod.js —
// dieselbe 1-Jahres-Frist-Regel wie Steuer-Engine (fifo.js) und Anzeige
// (summarizeLots/TaxTimeline). Hier nur noch importiert, nicht dupliziert.

/**
 * Baut den Alert-Snapshot aus der TX-Historie — über die EINE echte FIFO-Engine.
 * @param {Array} txs  Rohe Transaktionen (wie sie die App in `txs` hält)
 * @returns {{updatedAt:string, currency:string, holdings:Object, lots:Object}}
 */
export function computeAlertSnapshot(txs) {
  const prebuiltFifo = buildFifoPool(txs);
  const portfolio = calcPortfolio(txs, { prebuiltFifo });

  const holdings = {};
  const lots = {};
  const todayStr = new Date().toISOString().slice(0, 10);

  for (const [sym, stats] of Object.entries(portfolio)) {
    if (SNAPSHOT_EXCLUDE.has(sym)) continue;
    const id = COIN_MAP[sym]?.id;
    if (!id) continue; // ohne CoinGecko-ID nicht bepreisbar → für Cron nutzlos
    if (!(stats.menge > 0)) continue;

    holdings[id] = { sym, menge: stats.menge };

    // Haltefrist-Tranchen: nur noch nicht freie Lots mit bekanntem Kaufdatum.
    // Aggregiert pro freeDate, damit das Doc klein bleibt.
    const byFreeDate = {};
    for (const lot of (stats.lots || [])) {
      if (lot.costBasisUnknown) continue;            // Kaufdatum/Basis unklar → kein verlässlicher Alert
      const fd = lotFreeDate(lot.originalBuyDate);
      if (!fd || fd <= todayStr) continue;           // ungültig oder bereits frei
      const amt = Number(lot.remainingAmount || 0);
      if (!(amt > 0)) continue;
      byFreeDate[fd] = (byFreeDate[fd] || 0) + amt;
    }
    const tranches = Object.entries(byFreeDate)
      .map(([freeDate, menge]) => ({ freeDate, menge }))
      .sort((a, b) => (a.freeDate < b.freeDate ? -1 : 1));
    if (tranches.length) lots[id] = { sym, tranches };
  }

  return { updatedAt: new Date().toISOString(), currency: 'EUR', holdings, lots };
}

/**
 * Schreibt den Snapshot nach Firestore. No-op bei fehlendem uid / leerer Historie.
 */
export async function writeAlertSnapshot(uid, txs) {
  if (!uid || !Array.isArray(txs) || txs.length === 0) return;
  const snap = computeAlertSnapshot(txs);
  await setDoc(doc(db, 'users', uid, 'alertState', 'snapshot'), snap);
}

// Debounce: TX-Änderungen kommen oft in Schüben (Import). Wir wollen genau
// EINEN Snapshot-Write am Ende, nicht pro Einzel-TX.
let _timer = null;
export function scheduleAlertSnapshot(uid, txs, delay = 4000) {
  if (_timer) clearTimeout(_timer);
  _timer = setTimeout(() => {
    writeAlertSnapshot(uid, txs).catch((err) => {
      // Snapshot ist Best-Effort — ein Fehler darf die App nicht stören.
      // eslint-disable-next-line no-console
      console.warn('[alertSnapshot] Schreiben fehlgeschlagen:', err?.message);
    });
  }, delay);
}
